/**
 * Dr. Evil's Coffee — Halloween countdown override for Prestige <countdown-timer>.
 * Applies to BOTH the condensed header timer and the full "Countdown" section.
 * Overrides theme expires-at → next Oct 31 midnight (America/New_York).
 * On Oct 31: hide timers, show THE BEAST HAS AWAKENED in header copy.
 * After Halloween: counts down to next Oct 31 automatically, forever.
 *
 * Prestige caps day flips at 99 (Math.min(99, value)) and renders one digit per
 * character of the flip's initial text. We patch updateValue to pad to however
 * many digit slots the flip actually has (2 or 3), and rebuild the day flip with
 * 3 slots so 100+ days render correctly.
 *
 * v2 (2026-09-12): section timer now covered; slot-aware digit padding
 * (v1 forced 3 digits into the section's 2 slots → "049" displayed as "04").
 */
(function () {
  'use strict';

  var TZ = 'America/New_York';
  var BEAST_AWAKENED = '🎃 THE BEAST HAS AWAKENED 🎃';
  var BEAST_KEYWORDS = /halloween|beast|awaken/i;
  var DAY_FLIP_TEMPLATE = '000';

  // Every Prestige countdown-timer we manage: header (condensed) + main section.
  var TIMER_SELECTOR =
    'countdown-timer.countdown-condensed__timer, .countdown-condensed countdown-timer, countdown-timer.countdown__timer';

  var TEXT_SELECTORS =
    '.countdown-condensed__text .prose p, .countdown-condensed__text p.h6, .announcement-bar__message, .announcement-bar p';

  function getEasternParts(date) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    var parts = { year: 0, month: 0, day: 0 };

    fmt.formatToParts(date).forEach(function (part) {
      if (part.type === 'year') parts.year = Number(part.value);
      if (part.type === 'month') parts.month = Number(part.value);
      if (part.type === 'day') parts.day = Number(part.value);
    });

    return parts;
  }

  function isEasternHalloweenDay(now) {
    var p = getEasternParts(now || new Date());
    return p.month === 10 && p.day === 31;
  }

  function getTargetHalloweenYear(now) {
    var p = getEasternParts(now || new Date());

    if (p.month < 10 || (p.month === 10 && p.day < 31)) {
      return p.year;
    }

    return p.year + 1;
  }

  function getExpiresAtIso(year) {
    return year + '-10-31T00:00:00-04:00';
  }

  function forEachNode(selector, fn) {
    document.querySelectorAll(selector).forEach(fn);
  }

  function rememberOriginalText(el) {
    if (!el.dataset.drevilOriginalText) {
      el.dataset.drevilOriginalText = (el.textContent || '').trim();
    }
  }

  function shouldRewriteAnnouncement(el) {
    var stored = el.dataset.drevilOriginalText || '';
    var current = (el.textContent || '').trim();

    if (BEAST_KEYWORDS.test(stored) || BEAST_KEYWORDS.test(current)) {
      return true;
    }

    return el.closest('.countdown-condensed__text') != null;
  }

  /**
   * Prestige clamps days to 99 and assumes 2 digit slots. Patch to be slot-aware:
   * pad/clamp to the number of <countdown-timer-flip-digit> slots actually rendered
   * (3 for our rebuilt day flips, 2 for anything we haven't rebuilt yet).
   */
  function patchCountdownTimerFlip() {
    var Flip = window.customElements && customElements.get('countdown-timer-flip');
    if (!Flip || Flip.prototype.__drevilPatched) return;

    Flip.prototype.updateValue = function (value) {
      if (!this.shadowRoot) return;

      var slots = this.shadowRoot.children;
      var width = Math.max(1, slots.length);
      var max = Math.pow(10, width) - 1;
      var capped = Math.min(max, Math.max(0, Math.floor(value)));
      var digits = capped.toString().padStart(width, '0').slice(-width);

      for (var i = 0; i < digits.length && slots[i]; i++) {
        slots[i].setAttribute('number', digits.charAt(i));
      }
    };

    Flip.prototype.__drevilPatched = true;
  }

  function normalizeTimerHtml(html) {
    return html.replace(
      /(<countdown-timer-flip[^>]*\btype="days"[^>]*>)\s*\d+\s*(<\/countdown-timer-flip>)/gi,
      '$1' + DAY_FLIP_TEMPLATE + '$2'
    );
  }

  function getCountdownParts(iso) {
    var expiration = new Date(iso);
    var now = new Date();
    var diffMs = expiration.getTime() - now.getTime();

    if (diffMs <= 0) {
      return null;
    }

    var delta = diffMs / 1000;
    var days = Math.floor(delta / 86400);
    delta -= days * 86400;
    var hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;
    var minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;
    var seconds = Math.floor(delta % 60);

    return { days: days, hours: hours, minutes: minutes, seconds: seconds };
  }

  function forceFlipValues(timer, iso) {
    var parts = getCountdownParts(iso);
    if (!parts) return;

    timer.querySelectorAll('countdown-timer-flip').forEach(function (flip) {
      if (typeof flip.updateValue !== 'function') return;

      var type = flip.getAttribute('type');
      if (type === 'days') flip.updateValue(parts.days);
      if (type === 'hours') flip.updateValue(parts.hours);
      if (type === 'minutes') flip.updateValue(parts.minutes);
      if (type === 'seconds') flip.updateValue(parts.seconds);
    });
  }

  var applying = false;
  var scheduled = null;

  function setTimerVisible(visible) {
    forEachNode(TIMER_SELECTOR, function (timer) {
      timer.style.display = visible ? '' : 'none';

      if (visible) {
        timer.removeAttribute('hidden');
        timer.setAttribute('aria-hidden', 'false');
      } else {
        timer.setAttribute('hidden', '');
        timer.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function rebuildTimer(timer, iso, year) {
    if (timer.dataset.drevilExpires === iso) {
      forceFlipValues(timer, iso);
      return timer;
    }

    var replacement = document.createElement('countdown-timer');

    Array.from(timer.attributes).forEach(function (attr) {
      if (attr.name === 'expires-at' || attr.name === 'aria-label') return;
      replacement.setAttribute(attr.name, attr.value);
    });

    replacement.className = timer.className;
    replacement.innerHTML = normalizeTimerHtml(timer.innerHTML);
    replacement.setAttribute('expires-at', iso);
    replacement.setAttribute(
      'aria-label',
      'This offer expires on October 31, ' + year + ' at 12:00 am'
    );
    replacement.style.display = timer.style.display;

    if (timer.hasAttribute('hidden')) {
      replacement.setAttribute('hidden', '');
    }

    timer.replaceWith(replacement);
    replacement.dataset.drevilExpires = iso;

    customElements.whenDefined('countdown-timer').then(function () {
      forceFlipValues(replacement, iso);
    });

    return replacement;
  }

  function applyHalloweenMode() {
    setTimerVisible(false);

    forEachNode(TEXT_SELECTORS, function (el) {
      rememberOriginalText(el);
      if (!shouldRewriteAnnouncement(el)) return;
      if (el.textContent !== BEAST_AWAKENED) el.textContent = BEAST_AWAKENED;
    });
  }

  function applyCountdownMode() {
    var year = getTargetHalloweenYear();
    var iso = getExpiresAtIso(year);

    setTimerVisible(true);

    forEachNode(TIMER_SELECTOR, function (timer) {
      var active = rebuildTimer(timer, iso, year);
      forceFlipValues(active, iso);
    });

    forEachNode(TEXT_SELECTORS, function (el) {
      rememberOriginalText(el);
      if (!shouldRewriteAnnouncement(el)) return;

      var original = el.dataset.drevilOriginalText;
      if (original && (el.textContent || '').trim() !== original) {
        el.textContent = original;
      }
    });
  }

  function run() {
    if (applying) return;
    applying = true;

    try {
      if (isEasternHalloweenDay()) {
        applyHalloweenMode();
      } else {
        applyCountdownMode();
      }
    } finally {
      applying = false;
    }
  }

  function scheduleRun() {
    if (scheduled) return;

    scheduled = window.setTimeout(function () {
      scheduled = null;
      run();
    }, 150);
  }

  function boot() {
    patchCountdownTimerFlip();
    run();
    window.setInterval(run, 1000);

    // Re-apply when the theme editor / section rendering swaps DOM under us.
    if (window.MutationObserver) {
      new MutationObserver(scheduleRun).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  function start() {
    patchCountdownTimerFlip();

    if (window.customElements && typeof customElements.whenDefined === 'function') {
      customElements.whenDefined('countdown-timer-flip').then(function () {
        patchCountdownTimerFlip();
        return customElements.whenDefined('countdown-timer');
      }).then(boot).catch(boot);
      return;
    }

    boot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();